import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';

// User Profile Service
export const userService = {
  // Get user profile
  async getUserProfile(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  },

  // Create or update user profile
  async updateUserProfile(userId, userData) {
    try {
      await setDoc(doc(db, 'users', userId), {
        ...userData,
        updatedAt: serverTimestamp()
      }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  // Initialize user profile for new users
  async initializeUserProfile(userId, userData) {
    try {
      await setDoc(doc(db, 'users', userId), {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        role: 'user',
        isActive: true
      });
      
      // Initialize wallet
      await walletService.initializeWallet(userId);
      return true;
    } catch (error) {
      console.error('Error initializing user profile:', error);
      throw error;
    }
  }
};

// Wallet Service
export const walletService = {
  // Initialize wallet for new user
  async initializeWallet(userId) {
    try {
      await setDoc(doc(db, 'wallets', userId), {
        balance: 0,
        pendingEarnings: 0,
        totalEarnings: 0,
        totalSpent: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error initializing wallet:', error);
      throw error;
    }
  },

  // Get wallet data
  async getWallet(userId) {
    try {
      const walletDoc = await getDoc(doc(db, 'wallets', userId));
      return walletDoc.exists() ? { id: walletDoc.id, ...walletDoc.data() } : null;
    } catch (error) {
      console.error('Error getting wallet:', error);
      throw error;
    }
  },

  // Update wallet balance
  async updateBalance(userId, amount, type = 'add') {
    try {
      const walletRef = doc(db, 'wallets', userId);
      const updateData = {
        updatedAt: serverTimestamp()
      };

      if (type === 'add') {
        updateData.balance = increment(amount);
        updateData.totalEarnings = increment(amount);
      } else if (type === 'subtract') {
        updateData.balance = increment(-amount);
        updateData.totalSpent = increment(amount);
      }

      await updateDoc(walletRef, updateData);
      return true;
    } catch (error) {
      console.error('Error updating balance:', error);
      throw error;
    }
  },

  // Add transaction
  async addTransaction(userId, transactionData) {
    try {
      const transaction = {
        ...transactionData,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'transactions'), transaction);
      return docRef.id;
    } catch (error) {
      console.error('Error adding transaction:', error);
      throw error;
    }
  },

  // Get user transactions
  async getUserTransactions(userId, filters = {}) {
    try {
      let q = query(
        collection(db, 'transactions'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      if (filters.type) {
        q = query(q, where('type', '==', filters.type));
      }

      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting transactions:', error);
      throw error;
    }
  }
};

// Campaign Service
export const campaignService = {
  // Create campaign
  async createCampaign(campaignData) {
    try {
      const campaign = {
        ...campaignData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
        participants: 0,
        submissions: 0
      };

      const docRef = await addDoc(collection(db, 'campaigns'), campaign);
      return docRef.id;
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  },

  // Get campaigns
  async getCampaigns(filters = {}) {
    try {
      let q = query(collection(db, 'campaigns'));

      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.creatorId) {
        q = query(q, where('creatorId', '==', filters.creatorId));
      }

      q = query(q, orderBy('createdAt', 'desc'));

      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        deadline: doc.data().deadline?.toDate()
      }));
    } catch (error) {
      console.error('Error getting campaigns:', error);
      throw error;
    }
  },

  // Get single campaign
  async getCampaign(campaignId) {
    try {
      const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId));
      if (campaignDoc.exists()) {
        const data = campaignDoc.data();
        return {
          id: campaignDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          deadline: data.deadline?.toDate()
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting campaign:', error);
      throw error;
    }
  },

  // Update campaign
  async updateCampaign(campaignId, updateData) {
    try {
      await updateDoc(doc(db, 'campaigns', campaignId), {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  },

  // Join campaign
  async joinCampaign(campaignId, userId) {
    try {
      // Add user to campaign participants
      await addDoc(collection(db, 'campaignParticipants'), {
        campaignId,
        userId,
        joinedAt: serverTimestamp(),
        status: 'active'
      });

      // Increment participant count
      await updateDoc(doc(db, 'campaigns', campaignId), {
        participants: increment(1),
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Error joining campaign:', error);
      throw error;
    }
  },

  // Get user's joined campaigns
  async getUserJoinedCampaigns(userId) {
    try {
      const participantsQuery = query(
        collection(db, 'campaignParticipants'),
        where('userId', '==', userId),
        where('status', '==', 'active')
      );

      const participantsSnapshot = await getDocs(participantsQuery);
      const campaignIds = participantsSnapshot.docs.map(doc => doc.data().campaignId);

      if (campaignIds.length === 0) return [];

      // Get campaign details
      const campaigns = [];
      for (const campaignId of campaignIds) {
        const campaign = await this.getCampaign(campaignId);
        if (campaign) campaigns.push(campaign);
      }

      return campaigns;
    } catch (error) {
      console.error('Error getting joined campaigns:', error);
      throw error;
    }
  }
};

// Submission Service
export const submissionService = {
  // Create submission
  async createSubmission(submissionData) {
    try {
      const submission = {
        ...submissionData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'pending'
      };

      const docRef = await addDoc(collection(db, 'submissions'), submission);
      
      // Increment submission count in campaign
      await updateDoc(doc(db, 'campaigns', submissionData.campaignId), {
        submissions: increment(1),
        updatedAt: serverTimestamp()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating submission:', error);
      throw error;
    }
  },

  // Get user submissions
  async getUserSubmissions(userId) {
    try {
      const q = query(
        collection(db, 'submissions'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting user submissions:', error);
      throw error;
    }
  },

  // Get campaign submissions
  async getCampaignSubmissions(campaignId) {
    try {
      const q = query(
        collection(db, 'submissions'),
        where('campaignId', '==', campaignId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting campaign submissions:', error);
      throw error;
    }
  },

  // Update submission status
  async updateSubmissionStatus(submissionId, status, reviewData = {}) {
    try {
      await updateDoc(doc(db, 'submissions', submissionId), {
        status,
        ...reviewData,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating submission status:', error);
      throw error;
    }
  }
};

// Notification Service
export const notificationService = {
  // Create notification
  async createNotification(userId, notificationData) {
    try {
      const notification = {
        ...notificationData,
        userId,
        read: false,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'notifications'), notification);
      return docRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  },

  // Get user notifications
  async getUserNotifications(userId, limit = 20) {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limit)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting notifications:', error);
      throw error;
    }
  },

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        readAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }
};

// Real-time listeners
export const realtimeService = {
  // Listen to wallet changes
  subscribeToWallet(userId, callback) {
    return onSnapshot(doc(db, 'wallets', userId), (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      }
    });
  },

  // Listen to user notifications
  subscribeToNotifications(userId, callback) {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const notifications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      callback(notifications);
    });
  },

  // Listen to campaign changes
  subscribeToCampaigns(filters, callback) {
    let q = query(collection(db, 'campaigns'));

    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    q = query(q, orderBy('createdAt', 'desc'));

    return onSnapshot(q, (querySnapshot) => {
      const campaigns = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        deadline: doc.data().deadline?.toDate()
      }));
      callback(campaigns);
    });
  }
};

// Error handling helper
export const handleFirebaseError = (error) => {
  console.error('Firebase Error:', error);
  
  switch (error.code) {
    case 'permission-denied':
      return 'You do not have permission to perform this action.';
    case 'not-found':
      return 'The requested data was not found.';
    case 'already-exists':
      return 'This item already exists.';
    case 'unavailable':
      return 'Service is currently unavailable. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};
